import { useMemo } from 'react';

import { TierBadge } from '@/components/fleet/tier-badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { Playbook, ProvisioningTier } from '@/types/provisioning';

/** Ordered group headings — anything else is appended alphabetically after. */
const GROUP_ORDER = ['Deployment', 'Operations', 'Monitoring', 'Security', 'Lifecycle'];

function groupRank(group: string): number {
  const idx = GROUP_ORDER.indexOf(group);

  return idx === -1 ? GROUP_ORDER.length : idx;
}

/** Whether the current role may run a playbook of this tier. */
export function canRunTier(tier: ProvisioningTier, isAdmin: boolean): boolean {
  return tier === 'SAFE' || isAdmin;
}

/**
 * Left-hand playbook catalogue, grouped by `group`. DEPLOY/DESTRUCTIVE rows
 * are disabled for `support` users (tooltip explains why); DESTRUCTIVE rows
 * are disabled for everyone in PR-E #2 (enabled in PR-E #3).
 */
export function PlaybookCatalogue({
  playbooks,
  selectedId,
  isAdmin,
  onSelect,
}: {
  playbooks: Playbook[];
  selectedId: number | null;
  isAdmin: boolean;
  onSelect: (playbook: Playbook) => void;
}): JSX.Element {
  const grouped = useMemo(() => {
    const byGroup = new Map<string, Playbook[]>();

    for (const playbook of playbooks) {
      const list = byGroup.get(playbook.group) ?? [];

      list.push(playbook);
      byGroup.set(playbook.group, list);
    }

    return [...byGroup.entries()]
      .sort(([a], [b]) => groupRank(a) - groupRank(b) || a.localeCompare(b))
      .map(([group, list]) => ({
        group,
        list: [...list].sort((a, b) => a.name.localeCompare(b.name)),
      }));
  }, [playbooks]);

  return (
    <div className="space-y-4">
      {grouped.map(({ group, list }) => (
        <div key={group}>
          <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {group}
          </h3>
          <ul className="space-y-1">
            {list.map((playbook) => {
              // DESTRUCTIVE rows are enabled for admins in PR-E #3 — the run
              // panel gates them with a mandatory reason + type-to-confirm.
              const roleBlocked = !canRunTier(playbook.tier, isAdmin);
              const disabled = roleBlocked;
              const selected = playbook.id === selectedId;

              const reason = roleBlocked ? 'Read-only: admin role required' : null;

              const row = (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(playbook)}
                  aria-pressed={selected}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors',
                    selected
                      ? 'border-primary bg-accent'
                      : 'border-transparent hover:bg-accent/60',
                    disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
                  )}
                >
                  <span className="truncate">{playbook.name}</span>
                  <TierBadge tier={playbook.tier} />
                </button>
              );

              return (
                <li key={playbook.id}>
                  {reason ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={0} className="block">
                          {row}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{reason}</TooltipContent>
                    </Tooltip>
                  ) : (
                    row
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
